import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSession } from "@/auth/session";
import { getMySubscription, initSubscriptionCheckout, initKhipuCheckout, type SubscriptionInfo } from "@/lib/api";

type PaymentResult = "success" | "failed" | "cancelled";

const INTERVAL_LABEL: Record<string, string> = {
  monthly: "Mensual",
  annual: "Anual",
  permanent: "Permanente",
};

const STATUS_BADGE: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  suspended: "bg-red-100 text-red-700",
  pending: "bg-amber-100 text-amber-700",
  cancelled: "bg-gray-100 text-gray-500",
};

const STATUS_LABEL: Record<string, string> = {
  active: "Activa",
  suspended: "Suspendida",
  pending: "Pendiente",
  cancelled: "Cancelada",
};

const PAYMENT_STATUS_BADGE: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  pending: "Pendiente",
  paid: "Pagado",
  failed: "Fallido",
};

export default function SuscripcionTab() {
  const { session, refreshProfile } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState<"" | "webpay" | "khipu">("");
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [tbData, setTbData] = useState<{ token: string; url: string } | null>(null);
  const [paymentResult, setPaymentResult] = useState<PaymentResult | null>(() => {
    const value = searchParams.get("payment");
    return value === "success" || value === "failed" || value === "cancelled" ? value : null;
  });

  const load = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    try {
      const data = await getMySubscription(session);
      setSubscription(data);
    } catch {
      setError("No se pudo cargar la información de suscripción");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    const value = searchParams.get("payment");
    if (value !== "success" && value !== "failed" && value !== "cancelled") return;

    setPaymentResult(value);
    setPaying("");

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("payment");
    setSearchParams(nextParams, { replace: true });

    void refreshProfile().finally(() => {
      void load();
    });
  }, [load, refreshProfile, searchParams, setSearchParams]);

  useEffect(() => { void load(); }, [load]);

  // Auto-submit Transbank form after token is set
  useEffect(() => {
    if (tbData && formRef.current) {
      formRef.current.submit();
    }
  }, [tbData]);

  const handlePayWebpay = async () => {
    if (!session) return;
    setPaying("webpay");
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/configuracion`;
      const result = await initSubscriptionCheckout(session, returnUrl);
      setTbData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al iniciar el pago");
      setPaying("");
    }
  };

  const handlePayKhipu = async () => {
    if (!session) return;
    setPaying("khipu");
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/configuracion`;
      const result = await initKhipuCheckout(session, returnUrl);
      window.location.href = result.payment_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al iniciar el pago con Khipu");
      setPaying("");
    }
  };

  const fmt = (n: number) => `$${Number(n).toLocaleString("es-CL")}`;
  const fmtDate = (d: string) => new Date(d).toLocaleDateString("es-CL");

  const sub = subscription;
  const plan = sub?.plan;
  const isPermanent = sub?.interval === "permanent";
  const price = sub?.interval === "annual" ? Number(plan?.priceAnnual ?? 0) : Number(plan?.priceMonthly ?? 0);
  const pendingPayment = sub?.payments?.find((p) => p.status === "pending");
  const gateways = sub?.availableGateways ?? [];
  const hasWebpay = gateways.includes("webpay");
  const hasKhipu = gateways.includes("khipu");

  return (
    <div className="space-y-6">
      {/* Payment result banner */}
      {paymentResult === "success" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-checkbox-circle-fill text-emerald-600 text-xl" />
          <div>
            <p className="font-semibold text-emerald-800">Pago procesado exitosamente</p>
            <p className="text-sm text-emerald-600">Tu suscripción ha sido renovada. ¡Gracias!</p>
          </div>
        </div>
      )}
      {paymentResult === "failed" && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-close-circle-fill text-red-600 text-xl" />
          <div>
            <p className="font-semibold text-red-800">El pago fue rechazado</p>
            <p className="text-sm text-red-600">La transacción no pudo completarse. Puedes intentarlo nuevamente.</p>
          </div>
        </div>
      )}
      {paymentResult === "cancelled" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-close-circle-line text-amber-600 text-xl" />
          <div>
            <p className="font-semibold text-amber-800">Pago cancelado</p>
            <p className="text-sm text-amber-600">Cancelaste el pago. Puedes intentarlo cuando quieras.</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : !sub || !plan ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-400">
          <i className="ri-file-unknow-line text-4xl mb-2 block" />
          <p>No se encontró información de suscripción</p>
        </div>
      ) : (
        <>
          {/* Plan card */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-blue-200 uppercase tracking-wide">Plan contratado</p>
                  <h2 className="text-2xl font-bold mt-0.5">{plan.name}</h2>
                </div>
                <span className={`text-xs px-3 py-1.5 rounded-full font-semibold ${STATUS_BADGE[sub.status] ?? "bg-gray-100 text-gray-500"}`}>
                  {STATUS_LABEL[sub.status] ?? sub.status}
                </span>
              </div>
            </div>

            <div className="px-6 py-5 grid grid-cols-3 divide-x divide-gray-100">
              <div className="pr-6">
                <p className="text-xs text-gray-500 mb-1">Tipo de cobro</p>
                <p className="font-semibold text-gray-900">{INTERVAL_LABEL[sub.interval] ?? sub.interval}</p>
              </div>
              <div className="px-6">
                <p className="text-xs text-gray-500 mb-1">Monto</p>
                <p className="font-semibold text-gray-900">
                  {isPermanent ? "Licencia única" : `${fmt(price)} / ${sub.interval === "annual" ? "año" : "mes"}`}
                </p>
              </div>
              <div className="pl-6">
                <p className="text-xs text-gray-500 mb-1">Próximo cobro</p>
                <p className="font-semibold text-gray-900">
                  {sub.nextBillingDate ? fmtDate(sub.nextBillingDate) : "—"}
                </p>
              </div>
            </div>

            {/* Pay button — solo si hay cobro pendiente */}
            {!isPermanent && (
              <div className="px-6 pb-5">
                {pendingPayment ? (
                  <>
                    <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700 flex items-center gap-2">
                      <i className="ri-time-line" />
                      Cobro pendiente de <strong>{fmt(pendingPayment.amount)}</strong> con vencimiento {fmtDate(pendingPayment.dueDate)}
                    </div>
                    <div className={`grid gap-3 ${hasWebpay && hasKhipu ? "grid-cols-2" : "grid-cols-1"}`}>
                      {hasWebpay && (
                        <button
                          onClick={handlePayWebpay}
                          disabled={!!paying}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2 text-sm"
                        >
                          {paying === "webpay" ? (
                            <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block" />Redirigiendo...</>
                          ) : (
                            <><i className="ri-bank-card-line" />Webpay Plus</>
                          )}
                        </button>
                      )}
                      {hasKhipu && (
                        <button
                          onClick={handlePayKhipu}
                          disabled={!!paying}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2 text-sm"
                        >
                          {paying === "khipu" ? (
                            <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block" />Redirigiendo...</>
                          ) : (
                            <><i className="ri-smartphone-line" />Khipu</>
                          )}
                        </button>
                      )}
                    </div>
                    {(hasWebpay || hasKhipu) && (
                      <p className="text-xs text-gray-400 text-center mt-2">Pago seguro en línea</p>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <i className="ri-checkbox-circle-line text-base" />
                    Estás al día. No tienes cobros pendientes.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment history */}
          {sub.payments.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900 text-sm">Historial de pagos</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {["Vencimiento", "Monto", "Método", "Pagado", "Estado"].map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sub.payments.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600">{fmtDate(p.dueDate)}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{fmt(p.amount)}</td>
                      <td className="px-4 py-3 text-gray-500 capitalize">{p.paymentMethod ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-500">{p.paidAt ? fmtDate(p.paidAt) : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PAYMENT_STATUS_BADGE[p.status] ?? "bg-gray-100 text-gray-500"}`}>
                          {PAYMENT_STATUS_LABEL[p.status] ?? p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Hidden Transbank POST form */}
      {tbData && (
        <form
          ref={formRef}
          method="POST"
          action={tbData.url}
          style={{ display: "none" }}
        >
          <input type="hidden" name="token_ws" value={tbData.token} />
        </form>
      )}
    </div>
  );
}
